import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardMedia,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Paper,
  useMediaQuery,
  useTheme,
  IconButton
} from "@mui/material";
import {
  ExpandMore,
  Star,
  Phone,
  WhatsApp,
  AccessTime,
  LocationOn,
  LocalOffer
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";
import { useLocation } from "react-router-dom";



const TourDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

      const location = useLocation();     // ✅ FIRST
    const selectedTour = location.state; // ✅ THEN USE


  if (!selectedTour) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f4f6f8"
        }}
      >
        <Typography color="#065F46">Loading tour details...</Typography>
      </Box>
    );
  }

  const {
    title,
    titlemono,
    places,
    image, // From Card data
    price,  // From Card data
    rating, // From Card data
    duration, // Assuming this comes from tour data or we enrich it
    Overview,
    Highlights,
    IncludeExclude,
    HowtoReach,
    TermsPolicy,
    CancellationPolicy
  } = selectedTour;

  // WhatsApp Message Logic
  const whatsappMessage = `Hi, I am interested in the tour: ${title}. Please send me details.`;
  const whatsappLink = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;

  // ================= BUTTON COMPONENT =================
  const ActionButtons = () => (
    <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
      <Button
        variant="contained"
        startIcon={<Phone />}
        href="tel:+916230206562"
        sx={{
          flex: 1,
          py: 1.5,
          bgcolor: "#065F46",
          borderRadius: "12px",
          fontWeight: "bold",
          "&:hover": { bgcolor: "#044e38" }
        }}
      >
        Call Now
      </Button>
      <Button
        variant="contained"
        startIcon={<WhatsApp />}
        href={whatsappLink}
        target="_blank"
        sx={{
          flex: 1,
          py: 1.5,
          bgcolor: "#25D366",
          borderRadius: "12px",
          fontWeight: "bold",
          "&:hover": { bgcolor: "#1da851" }
        }}
      >
        Chat
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh", position: "relative" }}>
      
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 6 }, pb: { xs: 12, md: 8 } }}>
        <Grid container spacing={4}>

          {/* --- LEFT COLUMN (CONTENT) --- */}
          <Grid item xs={12} md={8}>
            
            {/* Header */}
            <Box mb={3}>
              <Chip 
                label={duration || "Flexible Duration"} 
                size="small" 
                icon={<AccessTime />}
                sx={{ mb: 1.5, bgcolor: "#ecfdf5", color: "#065F46", fontWeight: 700 }} 
              />
              <Typography variant="h3" fontWeight={800} color="#111827" sx={{ mb: 1, fontSize: { xs: "2rem", md: "2.5rem" } }}>
                {title}
              </Typography>
              <Typography variant="body1" color="#6b7280" sx={{ mb: 2, fontWeight: 500 }}>
                {titlemono}
              </Typography>
              
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Star sx={{ color: "#F59E0B", fontSize: 20 }} />
                  <Typography fontWeight="bold">{rating || "4.8"}</Typography>
                  <Typography variant="caption" color="#9ca3af">/ 5</Typography>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <Stack direction="row" spacing={0.5} alignItems="center" color="#059669">
                  <LocationOn fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>{places}</Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Hero Image */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: "24px",
                overflow: "hidden",
                mb: 6,
                border: "1px solid #e5e7eb",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)"
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {/* Fallback to card image if imageone is missing */}
                <CardMedia
                  component="img"
                  image={selectedTour.image || selectedTour.imageone}
                  alt={title}
                  sx={{ 
                    width: "100%", 
                    height: { xs: "250px", md: "450px" },
                    objectFit: "cover"
                  }}
                />
              </motion.div>
            </Paper>

            {/* Content Accordions */}
            <Box>
              {/* Overview */}
              <Box mb={4}>
                <Typography variant="h5" fontWeight={700} mb={2} color="#065F46">Trip Overview</Typography>
                <Typography variant="body1" color="#4b5563" lineHeight={1.8} whiteSpace="pre-line">
                  {Overview || "Discover the beauty of this destination with our expertly curated itinerary."}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Highlight */}
              <Box mb={4}>
                <Typography variant="h5" fontWeight={700} mb={2} color="#065F46">Highlights</Typography>
                <Typography variant="body1" color="#4b5563" lineHeight={1.8} whiteSpace="pre-line">
                  {Highlights || "Experience the culture, food, and breathtaking views on this journey."}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* FAQ/Info Accordions */}
              <Accordion defaultExpanded sx={{ boxShadow: 'none', border: '1px solid #f3f4f6', borderRadius: '8px !important', mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#065F46' }} />}>
                  <Typography fontWeight={600}>Inclusions & Exclusions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="#6b7280" whiteSpace="pre-line">
                    {IncludeExclude}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ boxShadow: 'none', border: '1px solid #f3f4f6', borderRadius: '8px !important', mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#065F46' }} />}>
                  <Typography fontWeight={600}>How to Reach</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="#6b7280" whiteSpace="pre-line">
                    {HowtoReach}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ boxShadow: 'none', border: '1px solid #f3f4f6', borderRadius: '8px !important' }}>
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#065F46' }} />}>
                  <Typography fontWeight={600}>Cancellation Policy</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="#6b7280" whiteSpace="pre-line">
                    {CancellationPolicy}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Grid>

          {/* --- RIGHT COLUMN (SIDEBAR - DESKTOP ONLY) --- */}
          <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block'},marginTop:{xs: '2px', md: '200px',lg:'200px'} }}>
            <Box sx={{ position: "sticky", top: 100 }}>
              <Paper sx={{ p: 3, borderRadius: "16px", border: "1px solid #e5e7eb" }}>
                
                {/* Price Card Look */}
                <Box mb={3} pb={3} borderBottom="1px dashed #e5e7eb">
                  <Typography variant="overline" sx={{ color: "#6b7280", fontWeight: 600 }}>
                    Price Per Person
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="#065F46">
                    {price}
                  </Typography>
                  <Typography variant="body2" color="#9ca3af">
                    Taxes included
                  </Typography>
                </Box>

                {/* Features List (Static for Demo, can be dynamic) */}
                <Stack spacing={1.5} mb={4}>
                  <Typography fontWeight={600} mb={1}>Package Includes:</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" color="#374151">
                    <FaCheckCircle color="#065F46" size={14} />
                    <Typography variant="body2">Meals as per itinerary</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" color="#374151">
                    <FaCheckCircle color="#065F46" size={14} />
                    <Typography variant="body2">Transportation (Private)</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" color="#374151">
                    <FaCheckCircle color="#065F46" size={14} />
                    <Typography variant="body2">Sightseeing & Driver</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" color="#374151">
                    <FaCheckCircle color="#065F46" size={14} />
                    <Typography variant="body2">24/7 Support</Typography>
                  </Stack>
                </Stack>

                <ActionButtons />

              </Paper>
            </Box>
          </Grid>

        </Grid>
      </Container>

      {/* --- MOBILE FIXED BUTTON BAR --- */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "white",
            p: 2,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
            zIndex: 1000,
            borderTop: "1px solid #e5e7eb"
          }}
        >
          <ActionButtons />
        </Box>
      )}
    </Box>
  );
};

export default TourDetails;